import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { ErrorableLoader } from '@src/components';
import { useGetNifOrderFullQuery } from '@src/generated/graphql';
import NotFoundPage from '@src/pages/NotFound.page';
import NifOrderLayout from './components/NifOrderLayout';
import { useNifOrderFlow } from './hooks/useNifOrderFlow';
import NifPrepareDocumentsPage from './steps/0-prepare-documents.page';
import NifPersonalDetailsPage from './steps/1-personal-details.page';
import NifProofOfIdentityPage from './steps/2-proof-of-identity.page';
import NifMinorBirthCertificatePage from './steps/3-minor-birth-certificate.page';
import NifIsPortuguesePage from './steps/4-is-portuguese.page';
import NifPortugueseBirthCertificatePage from './steps/5-portuguese-birth-certificate.page';
import NifParentsInformationPage from './steps/6-parents-information.page';
import NifResidentialAddressPage from './steps/7-residential-address.page';
import NifProofOfAddressPage from './steps/8-proof-of-address.page';
import NifReviewPage from './steps/9-review.page';
import { NifOrderStepPageProps } from './types/stepProps';

function EditNifOrderPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  const { data, error } = useGetNifOrderFullQuery({ variables: { id } });
  const nifOrder = data?.nifOrder;
  const flowResult = useNifOrderFlow({
    isNew: false,
    nifOrder,
  });
  const navigate = useNavigate();

  if (flowResult.type === 'loading' || !nifOrder) {
    return <ErrorableLoader error={error} />;
  }

  if (flowResult.type === 'redirect') {
    return <Navigate replace to={flowResult.to} />;
  }

  const stepProps: NifOrderStepPageProps = {
    nifOrder,
    previousUrl: flowResult.previousUrl,
    getNextUrl: flowResult.getNextUrl,
    navigateToNextStep(order) {
      if (!order) {
        throw new Error('Order is required');
      }
      void navigate(flowResult.getNextUrl(order));
    },
  };

  return (
    <Routes>
      <Route
        element={
          <NifOrderLayout
            onExitClick={() => navigate('/')}
            steps={flowResult.categoryItems}
            headerName={flowResult.currentCategoryName}
          />
        }
      >
        <Route
          path="prepare-documents"
          element={<NifPrepareDocumentsPage {...stepProps} />}
        />
        <Route
          path="personal-details"
          element={<NifPersonalDetailsPage {...stepProps} />}
        />
        <Route
          path="proof-of-identity"
          element={<NifProofOfIdentityPage {...stepProps} />}
        />
        <Route
          path="minor-birth-certificate"
          element={<NifMinorBirthCertificatePage {...stepProps} />}
        />
        <Route
          path="is-portuguese"
          element={<NifIsPortuguesePage {...stepProps} />}
        />
        <Route
          path="portuguese-birth-certificate"
          element={<NifPortugueseBirthCertificatePage {...stepProps} />}
        />
        <Route
          path="parents-information"
          element={<NifParentsInformationPage {...stepProps} />}
        />
        <Route
          path="residential-address"
          element={<NifResidentialAddressPage {...stepProps} />}
        />
        <Route
          path="proof-of-address"
          element={<NifProofOfAddressPage {...stepProps} />}
        />
        <Route path="review" element={<NifReviewPage {...stepProps} />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default EditNifOrderPage;
